/**
 * Patients API - extends the generated OpenAPI client
 *
 * Provides cache management for patient clinical info (demographics, medications, diagnoses)
 * with automatic invalidation when patient data is updated.
 */

import { enhancedApi } from '@store/generated/patients';

/**
 * Feature-specific tag types for patient cache management
 */
const PATIENT_TAG_TYPES = [
  'PatientClinicalInfo',
  'PatientCommunicationsPreferences',
  'PatientContext',
  'PatientPharmacies',
  'PatientInfo',
  'PatientModuleData',
  'PatientPageV3Bootstrap',
  // Owned by features/medications-workspace (the unified-history read); the
  // medication CRUD writes below must invalidate it so the unified card refreshes.
  'UnifiedMedications',
] as const;

/**
 * Patients API - extends the generated endpoints with cache tags
 */
export const patientsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_TAG_TYPES,
  endpoints: {
    /**
     * GET /v2/provider/patients/{patientId}/clinical-info
     * Fetches patient demographics + medications + diagnoses + last session
     */
    getV2ProviderPatientsByPatientIdClinicalInfo: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/medications
     * Fetches the patient's medication list with canonical UUIDs.
     */
    getV2ProviderPatientsByPatientIdMedications: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/allergies
     * Fetches the patient's allergy list with canonical UUIDs.
     */
    getV2ProviderPatientsByPatientIdAllergies: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/diagnoses
     * Fetches the patient's diagnosis list with canonical UUIDs.
     */
    getV2ProviderPatientsByPatientIdDiagnoses: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}
     * Updates patient demographics fields
     * Invalidates clinical info cache to trigger refetch
     */
    patchV2ProviderPatientsByPatientId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientPageV3Bootstrap' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/communications-preferences
     * Fetches patient communication preferences including preferred language
     */
    getV2ProviderPatientsByPatientIdCommunicationsPreferences: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/language
     * Updates patient preferred language (syncs to Stripe)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguage: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
      ],
    },

    /**
     * PUT /v2/provider/patients/{patientId}/communications-preferences/{preferenceKey}
     * Updates a specific communication preference (email, sms, phone_call)
     */
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKey: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientCommunicationsPreferences' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/info
     * Fetches all patient data in a single nested response
     */
    getV2ProviderPatientsByPatientIdInfo: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
      ],
    },

    /**
     * GET /v2/provider/patients/{patientId}/pharmacies
     * Fetches all known patient pharmacies, primary first.
     */
    getV2ProviderPatientsByPatientIdPharmacies: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPharmacies' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/diagnoses
     * Adds a diagnosis to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * diagnoses via the legacy v1 module-data endpoint (EncounterViewV2
     * diagnoses tab, patient context bar) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdDiagnoses: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/diagnoses/{diagnosisId}
     * Updates a diagnosis on the patient
     */
    patchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/diagnoses/{diagnosisId}
     * Removes a diagnosis from the patient
     */
    deleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/medications
     * Adds a medication to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * medications via the legacy v1 module-data endpoint (EncounterViewV2
     * med box, patient context bar) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdMedications: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
        { type: 'UnifiedMedications' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/medications/{medicationId}
     * Updates a medication on the patient
     */
    patchV2ProviderPatientsByPatientIdMedicationsAndMedicationId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
        { type: 'UnifiedMedications' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/medications/{medicationId}
     * Removes a medication from the patient
     */
    deleteV2ProviderPatientsByPatientIdMedicationsAndMedicationId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
        { type: 'UnifiedMedications' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/allergies
     * Adds an allergy to the patient
     *
     * Invalidates PatientModuleData/PatientContext so surfaces that read
     * allergies via the legacy v1 module-data endpoint (patient context
     * bar popover) refetch after a v2 write.
     */
    postV2ProviderPatientsByPatientIdAllergies: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/allergies/{allergyId}
     * Updates an allergy on the patient
     */
    patchV2ProviderPatientsByPatientIdAllergiesAndAllergyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * DELETE /v2/provider/patients/{patientId}/allergies/{allergyId}
     * Removes an allergy from the patient
     */
    deleteV2ProviderPatientsByPatientIdAllergiesAndAllergyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
        { type: 'PatientModuleData' as const, id: patientId },
        { type: 'PatientContext' as const, id: patientId },
      ],
    },

    /**
     * POST /v2/provider/patients/{patientId}/pharmacies
     * Adds a local or DoseSpot-linked pharmacy to the patient.
     */
    postV2ProviderPatientsByPatientIdPharmacies: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPharmacies' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },

    /**
     * PATCH /v2/provider/patients/{patientId}/pharmacies/{pharmacyId}
     * Updates pharmacy details or marks a preferred pharmacy.
     */
    patchV2ProviderPatientsByPatientIdPharmaciesAndPharmacyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPharmacies' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },
    /**
     * DELETE /v2/provider/patients/{patientId}/pharmacies/{pharmacyId}
     * Removes a pharmacy from the patient, including the DoseSpot patient-pharmacy link.
     */
    deleteV2ProviderPatientsByPatientIdPharmaciesAndPharmacyId: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPharmacies' as const, id: patientId },
        { type: 'PatientInfo' as const, id: patientId },
        { type: 'PatientClinicalInfo' as const, id: patientId },
      ],
    },
  },
});

// =============================================================================
// User-Friendly Hook Exports
// =============================================================================

// Queries
export const usePatientClinicalInfoQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useLazyPatientClinicalInfoQuery =
  patientsApi.useLazyGetV2ProviderPatientsByPatientIdClinicalInfoQuery;
export const useCommunicationsPreferencesQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdCommunicationsPreferencesQuery;
export const usePatientInfoQuery = patientsApi.useGetV2ProviderPatientsByPatientIdInfoQuery;
export const useMedicationsQuery = patientsApi.useGetV2ProviderPatientsByPatientIdMedicationsQuery;
export const useAllergiesQuery = patientsApi.useGetV2ProviderPatientsByPatientIdAllergiesQuery;
export const useDiagnosesQuery = patientsApi.useGetV2ProviderPatientsByPatientIdDiagnosesQuery;
export const usePatientPharmaciesQuery =
  patientsApi.useGetV2ProviderPatientsByPatientIdPharmaciesQuery;

// Mutations — patient update
export const useUpdatePatientMutation = patientsApi.usePatchV2ProviderPatientsByPatientIdMutation;
export const useUpdateLanguagePreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesLanguageMutation;
export const useUpdateCommunicationPreferenceMutation =
  patientsApi.usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKeyMutation;

// Mutations — clinical data CRUD
export const useAddDiagnosisMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdDiagnosesMutation;
export const useRemoveDiagnosisMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisIdMutation;
export const useAddMedicationMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdMedicationsMutation;
export const useRemoveMedicationMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdMedicationsAndMedicationIdMutation;
export const useAddAllergyMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdAllergiesMutation;
export const useRemoveAllergyMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdAllergiesAndAllergyIdMutation;
export const useUpdateDiagnosisMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdDiagnosesAndDiagnosisIdMutation;
export const useUpdateMedicationMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdMedicationsAndMedicationIdMutation;
export const useUpdateAllergyMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdAllergiesAndAllergyIdMutation;
export const useAddPatientPharmacyMutation =
  patientsApi.usePostV2ProviderPatientsByPatientIdPharmaciesMutation;
export const useUpdatePatientPharmacyMutation =
  patientsApi.usePatchV2ProviderPatientsByPatientIdPharmaciesAndPharmacyIdMutation;
export const useRemovePatientPharmacyMutation =
  patientsApi.useDeleteV2ProviderPatientsByPatientIdPharmaciesAndPharmacyIdMutation;

// =============================================================================
// Type Re-exports for Convenience
// =============================================================================

export type {
  AddAllergyRequest,
  AddDiagnosisRequest,
  AddMedicationRequest,
  AddPatientPharmacyRequest,
  AddressesSection,
  AllergyInfo2,
  AllergyListResponse,
  // Communication Preferences types
  CommunicationsPreferencesResponse,
  ConsentSection,
  ContactMethodPreference,
  ContactSection,
  DemographicsSection,
  DiagnosisInfo4,
  DiagnosisListResponse,
  EmergencyContactSection,
  EmploymentSection,
  InsuranceSummary2,
  MedicationInfo3,
  MedicationListResponse,
  PatientAddressesPatch,
  PatientAddressSchema,
  PatientClinicalInfo2 as PatientClinicalInfo,
  PatientClinicalInfoResponse,
  PatientContactPatch,
  PatientDemographicsPatch,
  PatientEmergencyContactPatch,
  PatientEmploymentPatch,
  // Patient Info types (full patient data)
  PatientInfoResponse,
  PatientPharmacyInfo4 as PatientPharmacyInfo,
  PatientPharmacyListResponse,
  PatientPharmacyResponse,
  UpdateAllergyRequest,
  UpdateCommunicationPreferenceRequest,
  UpdateDiagnosisRequest,
  UpdateLanguagePreferenceRequest,
  UpdateMedicationRequest,
  UpdatePatientPharmacyRequest,
  UpdatePatientRequest,
} from '@store/generated/patients';
